import React, { useState, useEffect } from "react";
import PasswordField from "./PasswordField";
import TextField from "./TextField";

/**
 * IntegrationModal - Dynamic modal that renders any integration config
 *
 * Features:
 * - Auto-generates form from integration config
 * - Handles all auth types
 * - Test connection functionality
 * - Save/cancel actions
 * - Loading states
 * - Success animations
 */
const IntegrationModal = ({
  integration,
  credentials = {},
  onSave,
  onClose,
  onTest,
  isSaving = false,
  isTesting = false,
  testResult = null,
  isLoadingCredentials = false,
  viewOnly = false, // When true, hide save button and make fields read-only
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize form data from existing credentials
  useEffect(() => {
    if (!isLoadingCredentials) {
      const initialData = {};
      integration.fields.forEach((field) => {
        initialData[field.name] = credentials[field.name] || "";
      });
      setFormData(initialData);
    }
  }, [integration, credentials, isLoadingCredentials]);

  // Handle field change
  const handleChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    integration.fields.forEach((field) => {
      const value = formData[field.name] || "";

      // Required field check
      if (field.required && !value.trim()) {
        newErrors[field.name] = `${field.label} is required`;
        return;
      }

      // Pattern validation
      if (field.validation?.pattern && value) {
        const regex = new RegExp(field.validation.pattern);
        if (!regex.test(value)) {
          newErrors[field.name] = field.validation.message || "Invalid format";
          return;
        }
      }

      // Length validation
      if (
        field.validation?.minLength &&
        value.length < field.validation.minLength
      ) {
        newErrors[field.name] =
          field.validation.message ||
          `Must be at least ${field.validation.minLength} characters`;
        return;
      }

      if (
        field.validation?.maxLength &&
        value.length > field.validation.maxLength
      ) {
        newErrors[field.name] =
          `Must be less than ${field.validation.maxLength} characters`;
        return;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const success = await onSave(formData);
    if (success) {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 1500);
    }
  };

  // Handle test connection
  const handleTest = async () => {
    if (!validateForm()) {
      return;
    }
    await onTest(formData);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "var(--bg-overlay)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        className="hf-panel hf-reveal-scale"
        style={{
          background: "var(--bg-modal)",
          borderRadius: "16px",
          maxWidth: "600px",
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          overflowX: "hidden",
          boxShadow: "var(--hf-shadow-3)",
          border: "1px solid var(--border-primary)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px",
            borderBottom: "1px solid var(--border-secondary)",
            position: "sticky",
            top: 0,
            background: "var(--bg-modal)",
            zIndex: 1,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "32px" }}>{integration.icon}</span>
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: 0, color: "var(--text-primary)", fontSize: "20px" }}>
                {integration.name}
              </h2>
              <p
                style={{
                  margin: "4px 0 0 0",
                  color: "var(--text-secondary)",
                  fontSize: "14px",
                }}
              >
                {integration.description}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: "var(--text-secondary)",
                cursor: "pointer",
                fontSize: "24px",
                padding: "4px",
                lineHeight: "1",
              }}
            >
              ✕
            </button>
          </div>

          {/* Affiliate CTA (if available) */}
          {integration.affiliate && integration.affiliate.commission && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                background: "var(--bg-tertiary)",
                borderRadius: "8px",
                border: "1px solid transparent",
              }}
            >
              <p
                style={{
                  margin: 0,
                  color: "var(--text-primary)",
                  fontSize: "13px",
                  marginBottom: "8px",
                }}
              >
                💡 {integration.affiliate.description}
              </p>
              <a
                href={integration.affiliate.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "6px 12px",
                  background: "var(--accent-primary)",
                  color: "var(--text-on-accent)",
                  textDecoration: "none",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                {integration.affiliate.cta} →
              </a>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSave}>
          <div style={{ padding: "24px" }}>
            {/* Loading credentials */}
            {isLoadingCredentials && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "20px",
                  textAlign: "center",
                  color: "var(--text-secondary)",
                  fontSize: "14px",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    width: "20px",
                    height: "20px",
                    border: "3px solid var(--border-primary)",
                    borderTop: "3px solid var(--accent-primary)",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "12px",
                  }}
                />
                <div>Loading credentials...</div>
              </div>
            )}

            {/* Success animation */}
            {showSuccess && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "16px",
                  background: "var(--bg-success-subtle)",
                  borderRadius: "8px",
                  color: "var(--accent-success)",
                  textAlign: "center",
                  fontSize: "14px",
                  fontWeight: "500",
                  animation: "fadeIn 0.3s ease",
                }}
              >
                ✅ {integration.messages.saveSuccess}
              </div>
            )}

            {/* Test result */}
            {testResult && !showSuccess && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "16px",
                  background: testResult.success ? "var(--bg-success-subtle)" : "var(--bg-danger-subtle)",
                  borderRadius: "8px",
                  color: "var(--text-primary)",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {testResult.success
                  ? integration.messages.testSuccess
                  : integration.messages.testFailure}
                {testResult.error && (
                  <div
                    style={{ marginTop: "8px", fontSize: "12px", opacity: 0.9 }}
                  >
                    {testResult.error}
                  </div>
                )}
              </div>
            )}

            {/* Dynamic fields */}
            {!isLoadingCredentials &&
              integration.fields.map((field) => {
                const FieldComponent =
                  field.type === "password" ? PasswordField : TextField;

                return (
                  <FieldComponent
                    key={field.name}
                    label={field.label}
                    value={formData[field.name] || ""}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    helpText={field.helpDetails || field.helpText}
                    helpIcon={field.helpIcon}
                    required={field.required}
                    error={errors[field.name]}
                    name={field.name}
                    type={field.type === "password" ? "password" : field.type}
                  />
                );
              })}

            {/* Capabilities list */}
            {integration.capabilities &&
              integration.capabilities.length > 0 && (
                <div style={{ marginTop: "24px" }}>
                  <h4
                    style={{
                      margin: "0 0 12px 0",
                      color: "var(--text-primary)",
                      fontSize: "14px",
                    }}
                  >
                    What you can do with {integration.name}:
                  </h4>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "20px",
                      color: "var(--text-secondary)",
                      fontSize: "13px",
                    }}
                  >
                    {integration.capabilities.map((capability, index) => (
                      <li key={index} style={{ marginBottom: "6px" }}>
                        {capability}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "24px",
              borderTop: "1px solid var(--border-secondary)",
              display: "flex",
              justifyContent: viewOnly ? "flex-end" : "space-between",
              gap: "12px",
              position: "sticky",
              bottom: 0,
              background: "var(--bg-modal)",
            }}
          >
            {/* Test Connection Button - only show if not view-only */}
            {!viewOnly && (
              <button
                type="button"
                onClick={handleTest}
                disabled={isTesting}
                style={{
                  padding: "12px 20px",
                  background: "var(--bg-tertiary)",
                  border: "1px solid var(--border-primary)",
                  borderRadius: "8px",
                  color: "var(--text-primary)",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: isTesting ? "not-allowed" : "pointer",
                  opacity: isTesting ? 0.6 : 1,
                }}
              >
                {isTesting ? "Testing..." : "🔌 Test Connection"}
              </button>
            )}

            <div style={{ display: "flex", gap: "12px" }}>
              {/* Close/Cancel */}
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                style={{
                  padding: "12px 20px",
                  background: viewOnly ? "var(--accent-primary)" : "transparent",
                  border: viewOnly ? "none" : "1px solid var(--border-primary)",
                  borderRadius: "8px",
                  color: viewOnly ? "var(--text-on-accent)" : "var(--text-secondary)",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                {viewOnly ? "Close" : "Cancel"}
              </button>

              {/* Save - only show if not view-only */}
              {!viewOnly && (
                <button
                  type="submit"
                  disabled={isSaving || showSuccess}
                  style={{
                    padding: "12px 20px",
                    background: "var(--accent-primary)",
                    border: "none",
                    borderRadius: "8px",
                    color: "var(--text-on-accent)",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: isSaving || showSuccess ? "not-allowed" : "pointer",
                    opacity: isSaving || showSuccess ? 0.6 : 1,
                  }}
                >
                  {isSaving
                    ? "Saving..."
                    : showSuccess
                      ? "✓ Saved"
                      : "Save Credentials"}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntegrationModal;
